/* ==========================================================================
   Virsaay — motion system
   One place that owns GSAP. Sections declare intent with data attributes and
   this file animates them, so no section ships its own fade-in code.

   Public API (window.VirsaayMotion):
     ready(fn)            run fn(gsap, ScrollTrigger) once GSAP + DOM are ready
     reduced              true when motion is off or the visitor asked for less
     dur(seconds)         scale a duration by the merchant's speed setting
     ease / easeExpo      shared easing strings
     register(el, st)     track a ScrollTrigger against an element for cleanup
     killWithin(root)     kill every tracked trigger inside root
     refresh()            debounced ScrollTrigger.refresh()
     on(name, fn) / off / emit(name, detail)
     lockScroll(bool)     reference-counted scroll lock
     trapFocus(el) / releaseFocus()
     announce(message)    polite live-region announcement
     splitLines(el)       wrap an element's text in per-line masks
     initRevealsIn(root)  wire the data-attribute reveals inside a subtree
   ========================================================================== */

(function () {
  'use strict';

  var config = (window.VirsaayTheme && window.VirsaayTheme.motion) || {
    enabled: true,
    speed: 1,
    parallax: true
  };

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hasGsap = typeof window.gsap !== 'undefined';

  var Motion = {
    ease: 'power3.out',
    easeExpo: 'expo.out',
    easeSoft: 'power2.out',
    reduced: !config.enabled || prefersReduced.matches || !hasGsap,
    speed: config.speed || 1,
    parallaxEnabled: config.parallax !== false
  };

  // When motion is off we still need the page to be fully visible. The stylesheet
  // hides [data-reveal] elements under `.js`, so lift that immediately.
  if (Motion.reduced) {
    document.documentElement.classList.add('virsaay-motion-off');
  }

  /* ----------------------------------------------------------------------
     Tiny event bus
     ---------------------------------------------------------------------- */

  var listeners = {};

  Motion.on = function (name, fn) {
    (listeners[name] = listeners[name] || []).push(fn);
    return fn;
  };

  Motion.off = function (name, fn) {
    if (!listeners[name]) return;
    listeners[name] = listeners[name].filter(function (candidate) {
      return candidate !== fn;
    });
  };

  Motion.emit = function (name, detail) {
    (listeners[name] || []).slice().forEach(function (fn) {
      try {
        fn(detail);
      } catch (error) {
        // A broken listener must not take the rest of the page down with it.
      }
    });
    document.dispatchEvent(new CustomEvent('virsaay:' + name, { detail: detail }));
  };

  /* ----------------------------------------------------------------------
     Readiness — every script is deferred, so GSAP has already parsed by the
     time this file runs. The polling path only matters if the CDN failed.
     ---------------------------------------------------------------------- */

  var readyQueue = [];
  var isReady = false;

  function flushReady() {
    if (isReady) return;
    isReady = true;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    if (gsap && ScrollTrigger && gsap.registerPlugin) {
      gsap.registerPlugin(ScrollTrigger);
      if (window.Flip) gsap.registerPlugin(window.Flip);
      // Defaults that suit the whole theme.
      gsap.defaults({ ease: Motion.ease, duration: Motion.dur(0.9) });
      ScrollTrigger.config({ ignoreMobileResize: true });
    }

    readyQueue.splice(0).forEach(function (fn) {
      try {
        fn(gsap, ScrollTrigger);
      } catch (error) {
        // Keep the remaining initialisers running.
      }
    });
  }

  Motion.ready = function (fn) {
    if (typeof fn !== 'function') return;
    if (isReady) {
      fn(window.gsap, window.ScrollTrigger);
      return;
    }
    readyQueue.push(fn);
  };

  function waitForGsap(attempt) {
    if (window.gsap && window.ScrollTrigger) {
      Motion.reduced = !config.enabled || prefersReduced.matches;
      if (Motion.reduced) document.documentElement.classList.add('virsaay-motion-off');
      flushReady();
      return;
    }
    if (attempt > 40) {
      // ~2s without GSAP: fall back to a static, fully visible page.
      Motion.reduced = true;
      document.documentElement.classList.add('virsaay-motion-off');
      document.documentElement.classList.remove('virsaay-loading');
      flushReady();
      return;
    }
    setTimeout(function () {
      waitForGsap(attempt + 1);
    }, 50);
  }

  Motion.dur = function (seconds) {
    if (Motion.reduced) return 0;
    var speed = Motion.speed || 1;
    return seconds / speed;
  };

  /* ----------------------------------------------------------------------
     ScrollTrigger bookkeeping — the theme editor mounts and unmounts sections
     constantly, and orphaned triggers are the classic Shopify memory leak.
     ---------------------------------------------------------------------- */

  var tracked = [];

  Motion.register = function (el, trigger) {
    if (!trigger) return trigger;
    tracked.push({ el: el, trigger: trigger });
    return trigger;
  };

  Motion.killWithin = function (root) {
    if (!root) return;
    tracked = tracked.filter(function (entry) {
      if (entry.el && (entry.el === root || root.contains(entry.el))) {
        if (entry.trigger.kill) entry.trigger.kill();
        else if (entry.trigger.revert) entry.trigger.revert();
        return false;
      }
      return true;
    });
  };

  var refreshTimer = null;

  Motion.refresh = function (delay) {
    if (!window.ScrollTrigger) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      // Sections create their pins from separate deferred scripts, so the
      // internal order is script order, not page order. A trigger below a pin
      // that refreshes before the pin never receives its pin spacing and fires
      // at the wrong scroll position. Sorting by start restores page order.
      if (window.ScrollTrigger.sort) window.ScrollTrigger.sort();
      window.ScrollTrigger.refresh();
    }, delay || 120);
  };

  /* ----------------------------------------------------------------------
     Scroll lock (reference counted so a drawer opened over a menu behaves)
     ---------------------------------------------------------------------- */

  var lockCount = 0;

  function scrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  Motion.lockScroll = function (shouldLock) {
    var root = document.documentElement;
    if (shouldLock) {
      if (lockCount === 0) {
        root.style.setProperty('--v-scrollbar', scrollbarWidth() + 'px');
        root.classList.add('virsaay-no-scroll');
      }
      lockCount += 1;
    } else {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) {
        root.classList.remove('virsaay-no-scroll');
        root.style.removeProperty('--v-scrollbar');
      }
    }
  };

  Motion.resetScrollLock = function () {
    lockCount = 0;
    document.documentElement.classList.remove('virsaay-no-scroll');
    document.documentElement.style.removeProperty('--v-scrollbar');
  };

  /* ----------------------------------------------------------------------
     Focus management for overlays
     ---------------------------------------------------------------------- */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var trapStack = [];

  function focusableWithin(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
    });
  }

  function onTrapKeydown(event) {
    if (event.key !== 'Tab') return;
    var current = trapStack[trapStack.length - 1];
    if (!current) return;

    var nodes = focusableWithin(current.el);
    if (!nodes.length) {
      event.preventDefault();
      return;
    }

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  Motion.trapFocus = function (el, initialFocus) {
    if (!el) return;
    // Re-trapping the same overlay moves it to the top instead of stacking it twice.
    trapStack = trapStack.filter(function (entry) {
      return entry.el !== el;
    });
    if (!trapStack.length) document.addEventListener('keydown', onTrapKeydown, true);
    trapStack.push({ el: el, returnTo: document.activeElement });

    var target = initialFocus || focusableWithin(el)[0] || el;
    // Wait a frame so the element is laid out and actually focusable.
    requestAnimationFrame(function () {
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    });
  };

  // Pass `el` to release that overlay's own trap. Overlays close on their own
  // timelines, so popping blindly would let a menu that finishes closing late
  // release the cart drawer's trap instead of its own.
  Motion.releaseFocus = function (restore, el) {
    var current = null;
    if (el) {
      for (var i = trapStack.length - 1; i >= 0; i--) {
        if (trapStack[i].el === el) {
          current = trapStack.splice(i, 1)[0];
          break;
        }
      }
      if (!current) return;
    } else {
      current = trapStack.pop();
    }
    if (!trapStack.length) document.removeEventListener('keydown', onTrapKeydown, true);
    if (current && restore !== false && current.returnTo && current.returnTo.focus) {
      try {
        current.returnTo.focus({ preventScroll: true });
      } catch (error) {
        current.returnTo.focus();
      }
    }
  };

  /* ----------------------------------------------------------------------
     Live region
     ---------------------------------------------------------------------- */

  Motion.announce = function (message) {
    var region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    // A tick of empty content makes screen readers re-announce repeats.
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  };

  /* ----------------------------------------------------------------------
     Line splitting — GSAP SplitText is a paid plugin, so this is a small
     word-wrap + offsetTop grouping implementation. Each line gets an overflow
     mask so the text can slide up from behind it.
     ---------------------------------------------------------------------- */

  Motion.splitLines = function (el) {
    if (!el || el.dataset.linesSplit === 'true') {
      return el ? Array.prototype.slice.call(el.querySelectorAll('.v-line__inner')) : [];
    }

    var source = el.textContent.replace(/\s+/g, ' ').trim();
    if (!source) return [];

    // Preserve the original markup so the split can be reverted if needed.
    if (!el.dataset.originalHtml) el.dataset.originalHtml = el.innerHTML;

    var words = source.split(' ');
    el.textContent = '';

    var fragment1 = document.createDocumentFragment();
    var wordNodes = words.map(function (word, i) {
      var span = document.createElement('span');
      span.className = 'v-word';
      span.style.display = 'inline-block';
      span.textContent = word + (i === words.length - 1 ? '' : ' ');
      fragment1.appendChild(span);
      return span;
    });
    el.appendChild(fragment1);

    // Group words into visual lines by their vertical offset.
    var lines = [];
    var currentTop = null;
    wordNodes.forEach(function (node) {
      var top = node.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        currentTop = top;
        lines.push([]);
      }
      lines[lines.length - 1].push(node);
    });

    el.textContent = '';
    var inners = [];
    var fragment2 = document.createDocumentFragment();

    lines.forEach(function (lineWords) {
      var mask = document.createElement('span');
      mask.className = 'v-line';
      mask.style.display = 'block';
      mask.style.overflow = 'hidden';
      // Display faces set taller than a tight line-height: without this
      // headroom the mask shaves capitals and descenders. The negative
      // margins keep the line spacing exactly as set.
      mask.style.padding = '0.14em 0 0.22em';
      mask.style.margin = '-0.14em 0 -0.22em';

      var inner = document.createElement('span');
      inner.className = 'v-line__inner';
      inner.style.display = 'block';
      inner.style.willChange = 'transform';
      inner.textContent = lineWords
        .map(function (node) {
          return node.textContent;
        })
        .join('');

      mask.appendChild(inner);
      fragment2.appendChild(mask);
      inners.push(inner);
    });

    el.appendChild(fragment2);

    el.dataset.linesSplit = 'true';
    return inners;
  };

  /* ----------------------------------------------------------------------
     Declarative reveals
     ---------------------------------------------------------------------- */

  function markDone(el) {
    el.dataset.revealDone = 'true';
  }

  function showStatically(root) {
    var nodes = root.querySelectorAll('[data-reveal], [data-reveal-clip], [data-reveal-lines]');
    Array.prototype.forEach.call(nodes, function (el) {
      el.style.opacity = '';
      el.style.clipPath = '';
      el.style.transform = '';
      markDone(el);
    });
  }

  function isManaged(el) {
    // The loader owns the hero's entrance; skip anything it will animate.
    return el.closest('[data-loader-managed]') !== null;
  }

  function initReveal(gsap, ScrollTrigger, el) {
    if (el.dataset.revealDone === 'true' || isManaged(el)) return;
    // Children of a stagger group are driven by the group, not individually.
    var group = el.parentElement && el.parentElement.closest('[data-reveal-stagger]');
    if (group && group.contains(el) && el.dataset.revealSolo !== 'true') return;

    var y = parseFloat(el.dataset.revealY || '34');
    var delay = parseFloat(el.dataset.revealDelay || '0');

    markDone(el);
    var tween = gsap.fromTo(
      el,
      { opacity: 0, y: y },
      {
        opacity: 1,
        y: 0,
        duration: Motion.dur(1),
        delay: delay,
        ease: Motion.ease,
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true
        }
      }
    );
    Motion.register(el, tween.scrollTrigger);
  }

  function initStagger(gsap, ScrollTrigger, group) {
    if (group.dataset.revealDone === 'true' || isManaged(group)) return;
    markDone(group);

    var children = Array.prototype.filter.call(group.children, function (child) {
      return child.hasAttribute('data-reveal') || group.dataset.revealStagger === 'all';
    });
    if (!children.length) children = Array.prototype.slice.call(group.children);
    if (!children.length) return;

    children.forEach(function (child) {
      markDone(child);
    });

    var step = parseFloat(group.dataset.revealStep || '0.09');
    var y = parseFloat(group.dataset.revealY || '46');

    var tween = gsap.fromTo(
      children,
      { opacity: 0, y: y },
      {
        opacity: 1,
        y: 0,
        duration: Motion.dur(1.05),
        ease: Motion.ease,
        stagger: step,
        scrollTrigger: {
          trigger: group,
          start: 'top 86%',
          once: true
        }
      }
    );
    Motion.register(group, tween.scrollTrigger);
  }

  function initClip(gsap, ScrollTrigger, el) {
    if (el.dataset.revealDone === 'true' || isManaged(el)) return;
    markDone(el);

    var inner = el.querySelector('img, video, .v-placeholder');
    var timeline = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: 'top 90%',
        once: true
      }
    });

    timeline.fromTo(
      el,
      { clipPath: 'inset(0 0 100% 0)' },
      { clipPath: 'inset(0 0 0% 0)', duration: Motion.dur(1.25), ease: Motion.easeExpo },
      0
    );

    if (inner) {
      // Must match the pre-animation state in virsaay-base.css
      // (`.js [data-reveal-clip] > img { transform: scale(1.12) }`). It used to
      // start from 1.14, which popped by 2% on the first frame.
      timeline.fromTo(
        inner,
        { scale: 1.12 },
        { scale: 1, duration: Motion.dur(1.6), ease: Motion.easeExpo },
        0
      );
    }

    Motion.register(el, timeline.scrollTrigger);
  }

  function initLines(gsap, ScrollTrigger, el) {
    if (el.dataset.revealDone === 'true' || isManaged(el)) return;
    var inners = Motion.splitLines(el);
    markDone(el);
    el.style.opacity = '1';
    if (!inners.length) return;

    var tween = gsap.fromTo(
      inners,
      // Far enough down to clear the mask's bottom headroom before the reveal.
      { yPercent: 150 },
      {
        yPercent: 0,
        duration: Motion.dur(1.15),
        ease: Motion.easeExpo,
        stagger: 0.075,
        scrollTrigger: {
          trigger: el,
          start: 'top 90%',
          once: true
        }
      }
    );
    Motion.register(el, tween.scrollTrigger);
  }

  function initParallax(gsap, ScrollTrigger, el) {
    if (!Motion.parallaxEnabled || el.dataset.parallaxDone === 'true') return;
    if (window.matchMedia('(max-width: 749px)').matches && el.dataset.parallaxMobile !== 'true') return;

    var inner = el.querySelector('img, video, .v-placeholder') || el;
    var strength = parseFloat(el.dataset.parallax || '0.12');
    if (!strength) return;

    el.dataset.parallaxDone = 'true';
    // Overscale so the parallax travel never exposes an edge.
    gsap.set(inner, { scale: 1 + Math.min(0.3, strength * 1.6) });

    var tween = gsap.fromTo(
      inner,
      { yPercent: -strength * 50 },
      {
        yPercent: strength * 50,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      }
    );
    Motion.register(el, tween.scrollTrigger);
  }

  Motion.initRevealsIn = function (root) {
    root = root || document;

    if (Motion.reduced) {
      showStatically(root);
      return;
    }

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) {
      showStatically(root);
      return;
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-reveal-stagger]'), function (el) {
      initStagger(gsap, ScrollTrigger, el);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-reveal-lines]'), function (el) {
      initLines(gsap, ScrollTrigger, el);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-reveal-clip]'), function (el) {
      initClip(gsap, ScrollTrigger, el);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-reveal]'), function (el) {
      initReveal(gsap, ScrollTrigger, el);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-parallax]'), function (el) {
      initParallax(gsap, ScrollTrigger, el);
    });
  };

  /* ----------------------------------------------------------------------
     Shared interaction polish — magnetic-ish button press and card lift.
     Kept deliberately restrained: luxury motion, not motion everywhere.
     ---------------------------------------------------------------------- */

  var interactionsReady = false;

  function initInteractions() {
    if (interactionsReady) return;
    interactionsReady = true;

    // Card hover lift used to live here as a delegated GSAP tween, but its
    // guard skipped `.vcard--editorial` and virsaay-product-card.liquid puts
    // that class on every card, so it never ran. The lift is a CSS transition
    // on `.vcard` now — composited, touch-gated, and cleaned up by the browser
    // rather than by per-card listeners that were never removed.

    // Card-row arrows are navigation, not decoration, so they work with
    // motion off too (the scroll is simply instant then).
    document.addEventListener('click', function (e) {
      var prevBtn = e.target.closest && e.target.closest('[data-row-prev]');
      var nextBtn = e.target.closest && e.target.closest('[data-row-next]');
      if (!prevBtn && !nextBtn) return;
      var row = (prevBtn || nextBtn).closest('[data-card-row]');
      var track = row && row.querySelector('[data-card-row-track]');
      if (!track) return;
      var item = track.querySelector('.virsaay-card-row__item') || track.firstElementChild;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 24;
      var shift = item ? item.offsetWidth + gap : 320;
      track.scrollBy({ left: prevBtn ? -shift : shift, behavior: Motion.reduced ? 'auto' : 'smooth' });
    });
  }

  /* Card-row counters. "01 / 04" follows the card at the leading edge of the
     track as it scrolls, and the arrows disable at either end. Any horizontal
     rail can opt in with the same attributes ([data-card-row] root,
     [data-card-row-track], [data-row-prev|next|current]); its items are the
     track's children. Rows whose track fits get `is-static` so CSS can drop
     the arrows rather than show two dead buttons. */
  function initRowCounters(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-card-row]'), function (row) {
      if (row.dataset.counterReady === 'true') return;
      var track = row.querySelector('[data-card-row-track]');
      if (!track) return;
      row.dataset.counterReady = 'true';

      var current = row.querySelector('[data-row-current]');
      var prev = row.querySelector('[data-row-prev]');
      var next = row.querySelector('[data-row-next]');
      var items = track.querySelectorAll('.virsaay-card-row__item');
      if (!items.length) items = track.children;
      var frame = null;

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function sync() {
        frame = null;
        var edge = track.getBoundingClientRect().left + (parseFloat(window.getComputedStyle(track).paddingLeft) || 0);
        var index = 0;
        var best = Infinity;
        Array.prototype.forEach.call(items, function (item, i) {
          var distance = Math.abs(item.getBoundingClientRect().left - edge);
          if (distance < best) {
            best = distance;
            index = i;
          }
        });
        var max = track.scrollWidth - track.clientWidth;
        // At the far end the last card may never reach the leading edge.
        if (max > 2 && track.scrollLeft >= max - 2) index = items.length - 1;
        if (current) current.textContent = pad(index + 1);
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = max <= 2 || track.scrollLeft >= max - 2;
        row.classList.toggle('is-static', max <= 2);
      }

      function schedule() {
        if (!frame) frame = window.requestAnimationFrame(sync);
      }

      track.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      Motion.register(row, {
        kill: function () {
          track.removeEventListener('scroll', schedule);
          window.removeEventListener('resize', schedule);
        }
      });
      sync();
    });
  }

  Motion.initRowCounters = initRowCounters;

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function boot() {
    // The loader owns the first paint on the homepage; reveals wait for it so
    // ScrollTrigger measures against the settled layout.
    var armed = window.VirsaayLoaderArmed === true;

    var start = function () {
      Motion.initRevealsIn(document);
      initInteractions();
      initRowCounters(document);
      Motion.refresh(200);
    };

    if (armed) {
      Motion.on('loader:done', start);
      // Safety net: if the loader never reports back, reveal anyway.
      setTimeout(function () {
        if (!isReady) return;
        if (document.documentElement.classList.contains('virsaay-loading')) return;
        start();
      }, 6000);
    } else {
      start();
    }

    // Images settling changes layout; re-measure once they are in.
    window.addEventListener('load', function () {
      Motion.refresh(120);
    });

    // Only re-measure when the width changes. On phones the address bar
    // showing / hiding fires a height-only resize on every scroll direction
    // change; refreshing then makes pinned sections jump mid-scroll.
    var resizeTimer = null;
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        Motion.refresh(0);
      }, 200);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        Motion.refresh(60);
      });
    }

    // Theme editor lifecycle.
    document.addEventListener('shopify:section:load', function (event) {
      Motion.initRevealsIn(event.target);
      initRowCounters(event.target);
      Motion.refresh(80);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      Motion.killWithin(event.target);
      Motion.refresh(80);
    });

    document.addEventListener('shopify:block:select', function () {
      Motion.refresh(60);
    });

    // Respond live when the visitor flips the OS setting.
    var onReducedChange = function () {
      if (prefersReduced.matches) {
        Motion.reduced = true;
        document.documentElement.classList.add('virsaay-motion-off');
        if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
        showStatically(document);
      }
    };

    if (prefersReduced.addEventListener) {
      prefersReduced.addEventListener('change', onReducedChange);
    } else if (prefersReduced.addListener) {
      prefersReduced.addListener(onReducedChange);
    }
  }

  Motion.ready(boot);

  window.VirsaayMotion = Motion;
  try {
    window.dispatchEvent(new CustomEvent('virsaay:motion:ready', { detail: Motion }));
  } catch (e) {}

  if (hasGsap) {
    flushReady();
  } else {
    waitForGsap(0);
  }
})();
